"use client"
import { Info } from "lucide-react"

import { Label } from "@/components/atoms/Label/label"
import Text from "@/components/atoms/Text/text"
import { AccordionSection } from "../Accordion"
import { RenderAmenities } from "@/components/molecules/Amenities"
import { formatPrice } from '@/utils/index'
import { Category, Amenity } from "@/app/types"
import { HeadingText } from "@/components/atoms/HeadingText/heading-text"

// Define the structure of aiMetadata based on API response
interface AiMetadata {
  propertyScore?: number
  investmentPotential?: number
  marketTrends?: {
    priceGrowth?: number
    demandScore?: number
    rentalYield?: number
  }
  valuationMetrics?: {
    pricePerSqFt?: number
    estimatedRentalIncome?: number
    futureValueEstimate?: number
  }
  qualityMetrics?: {
    neighborhoodScore?: number
    sustainabilityScore?: number
    renovationPotential?: number
  }
  keyFeatures?: string[]
}

interface PropertyDescriptionProps {
  property: {
    title: string
    description: string
    bedrooms: number
    bathrooms: number
    kitchens: number
    garages: number
    garageSize: number
    squareFeet: number
    status?: string
    category: Category | string
    price: number
    taxRate: number
    amenities: Amenity[] | string[]
    aiMetadata?: AiMetadata
  },
  translations: Record<string, any>
}

// Tooltip Component
const Tooltip = ({ text }: { text: string }) => (
  <span className="group relative ml-1 cursor-pointer text-gray-500">
    <Info size={14} />
    <span className="absolute bottom-full left-1/2 -translate-x-1/2 transform rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 z-50 w-48 text-center ring-1 ring-white/10 shadow-lg">
      {text}
    </span>
  </span>
)

// Utility function to filter out unavailable data
// We keep 0 as valid for scores, but might want to filter null/undefined
const filterAvailableData = <T extends Record<string, any>>(data: T): Partial<T> => {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined) {
      acc[key as keyof T] = value
    }
    return acc
  }, {} as Partial<T>)
}

export function PropertyDescription({ property, translations }: Readonly<any>) {
  const {
    description,
    garages,
    garageSize,
    category,
    taxRate,
    amenities,
    aiMetadata,
    bedrooms,
    bathrooms,
    kitchens,
    squareFeet,
  } = property

  const meta = aiMetadata as AiMetadata | undefined

  const ACCORDIONS = {
    [translations.heading.overview]: <div dangerouslySetInnerHTML={{ __html: description }} />,

    [translations.heading.propertyDetails]: (
      <div className="flex flex-col justify-around font-body">
        <div className="flex flex-col space-y-2">
          <div className="flex justify-between">
            <Text>{translations.bedrooms}:</Text>
            <Label>{bedrooms || 0}</Label>
          </div>
          <div className="flex justify-between">
            <Text>{translations.bathrooms}:</Text>
            <Label>{bathrooms || 0}</Label>
          </div>
          <div className="flex justify-between">
            <Text>{translations.kitchens}:</Text>
            <Label>{kitchens || 0}</Label>
          </div>
          <div className="flex justify-between">
            <Text>{translations.garage}:</Text>
            <Label>{garages || 0}</Label>
          </div>
          <div className="flex justify-between">
            <Text>{translations.lotSize}:</Text>
            <Label>{squareFeet ? `${formatPrice(squareFeet)} ${translations.sqft}` : 0}</Label>
          </div>
          <div className="flex justify-between">
            <Text>{translations.garageSize}:</Text>
            <Label>{garageSize ? `${formatPrice(garageSize)} ${translations.sqft}` : translations.noGarage}</Label>
          </div>
          <div className="flex justify-between">
            <Text>{translations.propertyType}:</Text>
            <Label>{(category as Category)?.name || "N/A"}</Label>
          </div>
          <div className="flex justify-between">
            <Text>{translations.taxRate}:</Text>
            <Label>{taxRate ? `${taxRate}%` : translations.notSpecified}</Label>
          </div>
        </div>
      </div>
    ),

    [translations.heading.amenities]: <RenderAmenities amenities={amenities as Amenity[]} />,

    [translations.heading.marketInsights]: (
      <div className="flex flex-col space-y-6">
        {!meta || Object.keys(meta).length === 0 ? (
          <div className="text-center text-gray-500">{translations.defaultMessage}</div>
        ) : (
          <>
            {/* Main Scores */}
            <div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4 dark:bg-zinc-800/50">
              {meta.propertyScore !== undefined && (
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="flex items-center gap-1 mb-1">
                    <Text className="font-semibold text-sm">{translations.propertyScoreLabel}</Text>
                    <Tooltip text="Overall quality score based on condition, location, and features." />
                  </div>
                  <div className="text-2xl font-bold text-primary">{meta.propertyScore}/100</div>
                </div>
              )}
              {meta.investmentPotential !== undefined && (
                <div className="flex flex-col items-center justify-center text-center border-l border-gray-200 dark:border-zinc-700">
                  <div className="flex items-center gap-1 mb-1">
                    <Text className="font-semibold text-sm">{translations.investmentPotentialLabel}</Text>
                    <Tooltip text="Score indicating potential for ROI and capital appreciation." />
                  </div>
                  <div className="text-2xl font-bold text-green-600">{meta.investmentPotential}/100</div>
                </div>
              )}
            </div>

            {/* Valuation Metrics */}
            {meta.valuationMetrics && (
              <div className="space-y-3">
                <HeadingText level={4} className="text-base font-semibold">{translations.valuationLabel}</HeadingText>
                <div className="space-y-2">
                  {meta.valuationMetrics.estimatedRentalIncome !== undefined && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Text>{translations.estRentalIncome}</Text>
                        <Tooltip text="Projected monthly rental income based on market data." />
                      </div>
                      <Label>${formatPrice(meta.valuationMetrics.estimatedRentalIncome)}/mo</Label>
                    </div>
                  )}
                  {meta.valuationMetrics.futureValueEstimate !== undefined && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Text>{translations.futureValue5y}</Text>
                        <Tooltip text="Estimated property value in 5 years based on current growth trends." />
                      </div>
                      <Label>${formatPrice(meta.valuationMetrics.futureValueEstimate)}</Label>
                    </div>
                  )}
                   {meta.valuationMetrics.pricePerSqFt !== undefined && meta.valuationMetrics.pricePerSqFt > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Text>{translations.pricePerSqFtLabel}</Text>
                      </div>
                      <Label>${formatPrice(meta.valuationMetrics.pricePerSqFt)}</Label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Market Trends */}
            {meta.marketTrends && (
              <div className="space-y-3">
                 <HeadingText level={4} className="text-base font-semibold">{translations.marketTrendsLabel}</HeadingText>
                 <div className="space-y-2">
                  {meta.marketTrends.priceGrowth !== undefined && (
                    <div className="flex items-center justify-between">
                      <Text>{translations.annualPriceGrowth}</Text>
                      <Label className={meta.marketTrends.priceGrowth >= 0 ? "text-green-600" : "text-red-500"}>
                        {meta.marketTrends.priceGrowth > 0 ? "+" : ""}{meta.marketTrends.priceGrowth}%
                      </Label>
                    </div>
                  )}
                  {meta.marketTrends.rentalYield !== undefined && (
                    <div className="flex items-center justify-between">
                      <Text>{translations.rentalYield}</Text>
                      <Label>{meta.marketTrends.rentalYield}%</Label>
                    </div>
                  )}
                  {meta.marketTrends.demandScore !== undefined && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                         <Text>{translations.marketDemand}</Text>
                         <Tooltip text="Based on search volume and days on market in this area." />
                      </div>
                      <Label>{meta.marketTrends.demandScore}/100</Label>
                    </div>
                  )}
                 </div>
              </div>
            )}

            {/* Quality Metrics */}
            {meta.qualityMetrics && (
              <div className="space-y-3">
                <HeadingText level={4} className="text-base font-semibold">{translations.qualityAssessmentLabel}</HeadingText>
                <div className="space-y-2">
                  {meta.qualityMetrics.neighborhoodScore !== undefined && (
                    <div className="flex items-center justify-between">
                      <Text>{translations.neighborhood}</Text>
                      <Label>{meta.qualityMetrics.neighborhoodScore}/100</Label>
                    </div>
                  )}
                  {meta.qualityMetrics.sustainabilityScore !== undefined && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                         <Text>{translations.sustainability}</Text>
                         <Tooltip text="Energy efficiency and environmental impact score." />
                      </div>
                      <Label>{meta.qualityMetrics.sustainabilityScore}/100</Label>
                    </div>
                  )}
                   {meta.qualityMetrics.renovationPotential !== undefined && (
                    <div className="flex items-center justify-between">
                      <Text>{translations.renovationPotential}</Text>
                      <Label>{meta.qualityMetrics.renovationPotential}/100</Label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Key Features List */}
            {meta.keyFeatures && Array.isArray(meta.keyFeatures) && meta.keyFeatures.length > 0 && (
              <div className="space-y-3">
                 <HeadingText level={4} className="text-base font-semibold">{translations.aiHighlightsLabel}</HeadingText>
                 <ul className="list-disc pl-5 space-y-1">
                   {meta.keyFeatures.map((feature, idx) => (
                     <li key={idx} className="text-sm text-gray-600 dark:text-gray-300">
                       {feature}
                     </li>
                   ))}
                 </ul>
              </div>
            )}

            <div className="text-xs italic text-gray-500 mt-4 pt-4 border-t border-gray-100 dark:border-zinc-800">
              * {translations.reliabilityDisclaimer}
            </div>
          </>
        )}
      </div>
    ),
  }

  return (
    <AccordionSection
      accordions={ACCORDIONS}
      defaultValue={[translations.heading.overview, translations.heading.propertyDetails, translations.heading.amenities, translations.heading.marketInsights]}
    />
  )
}
